import * as React from "react";
import "./styles.scss";
import { VeBoxCard } from "../../../../../components/card/VeBoxCard";
import { CensusCoolerContext } from "../../providers/CensusCollerProvider";
import {
	DefineEquipmentPageProviderContext,
	PAGES,
} from "../../providers/DefineEquipmentPageProvider";
import { DefineEquipmentCardFooter } from "../../components/DefineEquipmentCardFooter";
import { PeriodSelection } from "../../widgets/PeriodSelection";
import { FindEquipmentModal } from "../../widgets/FindEquipmentModal";
import { VeboxOverlayController } from "../../../../../components/overlay/VeboxOverlay";
import { EquipmentToCensusTable } from "../../widgets/EquipmentToCensusTable";
import { planAnalysisForSuggestedActions } from "../../data/controllers/CensusCoolerController";
import { AdviseModal } from "../../components/AdviseModal";
import { ImportEquipmentModal } from "../../widgets/ImportEquipmentModal";
import { Display } from "../../../../../components/Display";
import { AdviseAddFoundEquipmentModal } from "../../widgets/AdviseAddFoundEquipmentModal";
import { ImportEquipmentProvider } from "../../providers/ImportEquipmentProvider";
import { AppTranslations } from "../../../../../components/Utils/translations/AppTranslations";
import { FindEquipmentContext } from "../../providers/FindEquipmentProvider";

declare var AppTranslations: AppTranslations;

interface DefineEquipmentProps {
	overlayController: VeboxOverlayController;
}

export const DefineEquipmentPage: React.FC<DefineEquipmentProps> = ({
	overlayController,
}) => {
	const { selectedPeriod, census } = React.useContext(CensusCoolerContext);
	const { setIsRefreshingTable, isRefreshingTable, refreshList } =
		React.useContext(FindEquipmentContext);
	const {
		equipmentList,
		removeAllEquipment,
		totalCount,
		updateCurrentPage,
		fetchFirstEntriesList,
	} = React.useContext(DefineEquipmentPageProviderContext);

	const [showIsRequiredMessage, setShowIsRequiredMessage] =
		React.useState<boolean>(false);
	const [canSubmit, setCanSubmit] = React.useState<boolean>(true);
	const [isFirstSubmitClick, setIsFirstSubmitClick] =
		React.useState<boolean>(true);
	const [isGoingToNextStep, setIsGoingToNextStep] =
		React.useState<boolean>(false);
	const [isRemovingAll, setIsRemovingAll] = React.useState<boolean>(false);

	const removeAll = async () => {
		setIsRefreshingTable(true);
		setIsRemovingAll(true);
		await removeAllEquipment();
		setIsRemovingAll(false);
		setIsRefreshingTable(false);
	};

	const goToNext = () => {
		overlayController.clear();
		const callback = () => updateCurrentPage(PAGES.ELABORATION);
		setIsGoingToNextStep(true);
		planAnalysisForSuggestedActions(
			census?.guid,
			selectedPeriod?.startDate,
			selectedPeriod?.endDate,
		)
			.then(callback)
			.catch((error) => console.error(error));
	};

	const showPeriodRequiredMessage = (show: boolean) => {
		if (show !== showIsRequiredMessage) {
			setShowIsRequiredMessage(show);
		}
	};

	const isPeriodValid = () => {
		if (selectedPeriod !== null && selectedPeriod !== undefined) {
			showPeriodRequiredMessage(false);
			return true;
		} else {
			showPeriodRequiredMessage(true);
			return false;
		}
	};

	const closeFindEquipmentModal = async () => {
		overlayController.clear();
	};

	const closeImportEquipmentModal = (doNothing: boolean) => {
		if (!doNothing) {
			showFindEquipmentDialog("advise");
		} else {
			overlayController.clear();
		}
	};

	const closeAdviseAddFoundEquipmentModal = async () => {
		overlayController.clear();
	};

	const showFindEquipmentDialog = (show: "find" | "import" | "advise") => {
		overlayController.show(
			<>
				<Display isVisible={show === "find"}>
					<FindEquipmentModal closeDialog={closeFindEquipmentModal} />
				</Display>
				<ImportEquipmentProvider
					refreshList={refreshList}
					fetchFirstEntriesList={fetchFirstEntriesList}
				>
					<Display isVisible={show === "import"}>
						<ImportEquipmentModal closeDialog={closeImportEquipmentModal} />
					</Display>
					<Display isVisible={show === "advise"}>
						<AdviseAddFoundEquipmentModal
							onClose={closeFindEquipmentModal}
							onSubmit={closeAdviseAddFoundEquipmentModal}
						/>
					</Display>
				</ImportEquipmentProvider>
			</>,
		);
	};

	const closeAdviseModal = () => {
		overlayController.clear();
	};
	const showAdviseDialog = () => {
		setIsFirstSubmitClick(false);
		if (canSubmit && (!isFirstSubmitClick || isPeriodValid())) {
			overlayController.show(
				<AdviseModal onClose={closeAdviseModal} onSubmit={goToNext} />,
			);
		}
	};

	React.useEffect(() => {
		let submit = true;
		if (!isFirstSubmitClick) {
			submit = isPeriodValid();
		}
		setCanSubmit(submit && equipmentList?.length > 0);
	}, [selectedPeriod, equipmentList?.length, isFirstSubmitClick]);

	return (
		<VeBoxCard className={"equipment-list-card"}>
			<div className={"flx-row flx-space-between"}>
				<PeriodSelection showIsRequiredMessage={showIsRequiredMessage} />
				<h2 className={"equipment-list-count"}>
					{totalCount ?? 0}{" "}
					{totalCount === 1
						? AppTranslations.massiveActions.massiveActionsCommon.equipmentLabel
						: AppTranslations.massiveActions.massiveActionsCommon
								.equipmentLabelPlural}
				</h2>
			</div>
			<EquipmentToCensusTable isRefreshingTable={isRefreshingTable} />
			<DefineEquipmentCardFooter
				onReset={removeAll}
				onNext={showAdviseDialog}
				disabled={!canSubmit}
				onAdd={() => showFindEquipmentDialog("find")}
				onImport={() => showFindEquipmentDialog("import")}
				disableActionsButtons={isGoingToNextStep}
				disableReset={equipmentList?.length === 0}
				isResetLoading={isRemovingAll}
			/>
		</VeBoxCard>
	);
};
